import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { useCartStore, useUIStore } from "@/lib/stores";
import {
    Check, Star, Clock, Store, ShieldCheck, Zap, Heart, ChevronDown, ChevronUp,
} from "lucide-react";
import ProductCard from "@/components/ProductCard";
import BrandLogo from "@/components/BrandLogo";
import SEO, { productSchema, breadcrumbSchema, faqSchema } from "@/components/SEO";
import toast from "react-hot-toast";

export default function ProductDetail() {
    const { slug } = useParams();
    const nav = useNavigate();
    const [brand, setBrand] = useState(null);
    const [similar, setSimilar] = useState([]);
    const [faqs, setFaqs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [denom, setDenom] = useState(null);
    const [qty, setQty] = useState(1);
    const [faqOpen, setFaqOpen] = useState(0);
    const [tcOpen, setTcOpen] = useState(false);
    const [stepOpen, setStepOpen] = useState(true);
    const { addItem } = useCartStore();
    const { setCartOpen } = useUIStore();

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const r = await api.get(`/brands/${slug}`);
                setBrand(r.data.brand);
                setSimilar(r.data.similar);
                setFaqs(r.data.faqs);
                setDenom(r.data.brand.suggested_denominations[0] || r.data.brand.min_denomination);
                window.scrollTo({ top: 0, behavior: "smooth" });
            } catch {
                nav("/catalog");
            } finally {
                setLoading(false);
            }
        })();
    }, [slug, nav]);

    if (loading || !brand) {
        return (
            <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 py-10">
                <div className="grid md:grid-cols-2 gap-8">
                    <div className="aspect-square rk-shimmer rounded-3xl" />
                    <div className="space-y-4">
                        <div className="h-6 rk-shimmer rounded" />
                        <div className="h-12 rk-shimmer rounded" />
                        <div className="h-20 rk-shimmer rounded" />
                    </div>
                </div>
            </div>
        );
    }

    const payable = denom * qty * (1 - (brand.discount_percentage || 0) / 100);
    const inRange = denom >= brand.min_denomination && denom <= brand.max_denomination;

    const onAdd = () => {
        if (!inRange) { toast.error("Denomination out of range"); return; }
        addItem({
            brand_id: brand.id,
            sku: brand.sku,
            brand_name: brand.brand_name,
            logo_url: brand.logo_url,
            category: brand.category,
            denomination: Number(denom),
            quantity: qty,
            discount_percentage: brand.discount_percentage,
        });
        toast.success(`Added ${brand.brand_name} to cart`);
        setCartOpen(true);
    };

    return (
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 py-6 md:py-10">
            <SEO
                title={`${brand.brand_name} Gift Card — Up to ${brand.discount_percentage || 0}% Off`}
                description={`Buy ${brand.brand_name} gift card online. Denominations from ₹${brand.min_denomination} to ₹${brand.max_denomination}. Instant WhatsApp delivery, ${brand.validity || "1 Year"} validity, 100% genuine. ${brand.discount_percentage ? `Save up to ${brand.discount_percentage}% off face value.` : ""}`}
                path={`/brand/${slug}`}
                image={brand.logo_url}
                type="product"
                keywords={`${brand.brand_name} gift card, ${brand.brand_name} voucher, ${brand.category} gift card, buy ${brand.brand_name} gift card online`}
                structuredData={[
                    productSchema(brand),
                    breadcrumbSchema([
                        { name: "Home", path: "/" },
                        { name: "Catalog", path: "/catalog" },
                        { name: brand.category, path: `/catalog?category=${encodeURIComponent(brand.category)}` },
                        { name: brand.brand_name, path: `/brand/${slug}` },
                    ]),
                    ...(faqs && faqs.length ? [faqSchema(faqs)] : []),
                ]}
            />
            <nav className="text-xs text-[#6C6F7A] mb-5">
                <a href="/" className="hover:text-[#0B1437]">Home</a> / <a href="/catalog" className="hover:text-[#0B1437]">Catalog</a> /{" "}
                <a href={`/catalog?category=${encodeURIComponent(brand.category)}`} className="hover:text-[#0B1437]">{brand.category}</a> /{" "}
                <span className="text-[#0B1437]">{brand.brand_name}</span>
            </nav>

            <div className="grid md:grid-cols-12 gap-8">
                {/* Left: hero card */}
                <div className="md:col-span-5">
                    <div className="md:sticky md:top-24">
                        <div className="rk-logo-box aspect-square flex items-center justify-center p-6 md:p-8 relative overflow-hidden border border-rk-border bg-white">
                            <BrandLogo brand={brand} size="xl" />
                            {brand.discount_percentage > 0 && (
                                <div className="absolute top-5 right-5 bg-rk-navy text-rk-gold px-5 py-3 border border-rk-gold/50">
                                    <div className="text-[9px] uppercase tracking-[0.3em] text-rk-gold-soft">Save</div>
                                    <div className="font-serif text-3xl font-semibold">{brand.discount_percentage}%</div>
                                </div>
                            )}
                        </div>
                        {/* Gallery (admin-provided scrollable images) */}
                        {Array.isArray(brand.gallery_urls) && brand.gallery_urls.length > 0 && (
                            <div data-testid="brand-gallery" className="mt-4 flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
                                {brand.gallery_urls.map((u, i) => (
                                    <img
                                        key={i}
                                        src={u}
                                        alt={`${brand.brand_name} ${i+1}`}
                                        loading="lazy"
                                        className="h-28 w-auto rounded-lg border border-rk-border shrink-0 snap-start object-cover"
                                    />
                                ))}
                            </div>
                        )}
                        <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
                            <div className="bg-white border border-[#E5E0D5] rounded-xl p-3 text-center">
                                <Zap className="w-4 h-4 mx-auto text-[#0B1437]" />
                                <div className="mt-1 font-semibold">Instant</div>
                                <div className="text-[#6C6F7A]">Delivery</div>
                            </div>
                            <div className="bg-white border border-[#E5E0D5] rounded-xl p-3 text-center">
                                <ShieldCheck className="w-4 h-4 mx-auto text-[#1D5C42]" />
                                <div className="mt-1 font-semibold">100%</div>
                                <div className="text-[#6C6F7A]">Genuine</div>
                            </div>
                            <div className="bg-white border border-[#E5E0D5] rounded-xl p-3 text-center">
                                <Clock className="w-4 h-4 mx-auto text-[#0B1437]" />
                                <div className="mt-1 font-semibold">{brand.validity}</div>
                                <div className="text-[#6C6F7A]">Validity</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right: info + actions */}
                <div className="md:col-span-7 space-y-6">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-[#6C6F7A] mb-2">{brand.category}</div>
                        <h1 className="font-serif text-3xl md:text-5xl text-[#0B1437]">{brand.brand_name} Gift Card</h1>
                        <div className="flex items-center gap-3 mt-3 text-sm text-[#6C6F7A]">
                            <span className="inline-flex items-center gap-1 text-[#0B1437]">
                                <Star className="w-4 h-4 fill-[#B88A1A] text-[#B88A1A]" /> {brand.rating?.toFixed(1)}
                            </span>
                            <span className="w-px h-4 bg-[#E5E0D5]" />
                            <span className="inline-flex items-center gap-1"><Store className="w-3.5 h-3.5" /> {brand.acceptance}</span>
                            <span className="w-px h-4 bg-[#E5E0D5]" />
                            <span>SKU {brand.sku}</span>
                        </div>
                    </div>

                    {/* Denomination picker */}
                    <div>
                        <div className="flex items-baseline justify-between mb-3">
                            <span className="text-[11px] uppercase tracking-[0.22em] text-[#6C6F7A]">Choose denomination</span>
                            <span className="text-xs text-[#6C6F7A]">
                                ₹{brand.min_denomination.toLocaleString("en-IN")} — ₹{brand.max_denomination.toLocaleString("en-IN")}
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {brand.suggested_denominations.map((d) => (
                                <button
                                    key={d}
                                    data-testid={`denom-${d}`}
                                    onClick={() => setDenom(d)}
                                    className={`px-4 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${
                                        Number(denom) === d
                                            ? "border-[#0B1437] bg-[#0B1437] text-white"
                                            : "border-[#E5E0D5] hover:border-[#0B1437] bg-white"
                                    }`}
                                >
                                    ₹{d.toLocaleString("en-IN")}
                                </button>
                            ))}
                            <div className="inline-flex items-stretch rounded-xl border-2 border-[#E5E0D5] overflow-hidden focus-within:border-[#0B1437]">
                                <span className="bg-[#EFECE2] px-3 flex items-center text-sm font-semibold text-[#6C6F7A]">₹</span>
                                <input
                                    data-testid="custom-denom-input"
                                    type="number"
                                    value={denom}
                                    onChange={(e) => setDenom(e.target.value)}
                                    placeholder="Custom"
                                    className="w-28 px-3 text-sm outline-none"
                                />
                            </div>
                        </div>
                        {!inRange && (
                            <p className="text-xs text-[#0B1437] mt-2">
                                Please enter an amount between ₹{brand.min_denomination} and ₹{brand.max_denomination}
                            </p>
                        )}
                    </div>

                    {/* Quantity */}
                    <div className="flex items-center gap-4">
                        <span className="text-[11px] uppercase tracking-[0.22em] text-[#6C6F7A]">Quantity</span>
                        <div className="inline-flex items-center border border-rk-navy/20">
                            <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-10 h-10 text-lg hover:bg-rk-cream">−</button>
                            <span data-testid="qty-display" className="w-12 text-center font-serif text-lg">{qty}</span>
                            <button onClick={() => setQty((q) => Math.min(50, q + 1))} className="w-10 h-10 text-lg hover:bg-rk-cream">+</button>
                        </div>
                    </div>

                    {/* Summary + CTA */}
                    <div className="bg-white border border-[#E5E0D5] rounded-2xl p-5 flex items-center justify-between flex-wrap gap-4">
                        <div>
                            <div className="text-xs text-[#6C6F7A]">You pay</div>
                            <div className="flex items-baseline gap-2">
                                <span className="font-serif text-4xl font-bold text-[#0B1437]" data-testid="price-display">
                                    {formatINR(payable)}
                                </span>
                                {brand.discount_percentage > 0 && (
                                    <span className="text-sm line-through text-[#6C6F7A]">
                                        {formatINR(denom * qty)}
                                    </span>
                                )}
                            </div>
                            {brand.discount_percentage > 0 && (
                                <div className="text-xs text-[#1D5C42] font-semibold mt-1">
                                    You save {formatINR(denom * qty - payable)}
                                </div>
                            )}
                        </div>
                        <div className="flex gap-3">
                            <button data-testid="wishlist-btn" className="rk-btn-ghost inline-flex items-center gap-2 !px-4">
                                <Heart className="w-4 h-4" /> Save
                            </button>
                            <button data-testid="add-to-cart-btn" onClick={onAdd} className="rk-btn-primary inline-flex items-center gap-2 !text-base !px-6 !py-3">
                                <Zap className="w-4 h-4" /> Add to Cart
                            </button>
                        </div>
                    </div>

                    {/* Description */}
                    <div>
                        <h3 className="font-serif text-2xl mb-2">About this gift card</h3>
                        <p className="text-sm text-[#6C6F7A] leading-relaxed whitespace-pre-wrap">
                            {brand.description || `${brand.brand_name} Gift Card — the perfect gift for every occasion.`}
                        </p>
                    </div>

                    {/* Redemption steps */}
                    <Accordion
                        open={stepOpen}
                        onToggle={() => setStepOpen((o) => !o)}
                        title="How to redeem"
                        testid="acc-redeem"
                    >
                        <div className="text-sm text-[#6C6F7A] whitespace-pre-wrap leading-relaxed">
                            {brand.redemption_steps || `1. Visit ${brand.brand_name} store or website\n2. At checkout, select "Pay with Gift Card"\n3. Enter the 16-digit voucher code and PIN\n4. Enjoy!`}
                        </div>
                    </Accordion>

                    {/* Terms */}
                    {brand.terms_and_conditions && (
                        <Accordion
                            open={tcOpen}
                            onToggle={() => setTcOpen((o) => !o)}
                            title="Terms & Conditions"
                            testid="acc-tc"
                        >
                            <div className="text-xs text-[#6C6F7A] whitespace-pre-wrap leading-relaxed max-h-72 overflow-y-auto">
                                {brand.terms_and_conditions}
                            </div>
                        </Accordion>
                    )}

                    {/* FAQs */}
                    <div>
                        <h3 className="font-serif text-2xl mb-3">Frequently asked questions</h3>
                        <div className="space-y-2">
                            {faqs.map((f, i) => (
                                <Accordion
                                    key={i}
                                    open={faqOpen === i}
                                    onToggle={() => setFaqOpen(faqOpen === i ? -1 : i)}
                                    title={f.q}
                                    testid={`faq-${i}`}
                                >
                                    <p className="text-sm text-[#6C6F7A] leading-relaxed">{f.a}</p>
                                </Accordion>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Similar */}
            {similar.length > 0 && (
                <section className="mt-14 border-t border-[#E5E0D5] pt-10">
                    <h3 className="font-serif text-3xl mb-5">You might also like</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {similar.slice(0, 4).map((b) => <ProductCard key={b.id} brand={b} />)}
                    </div>
                </section>
            )}
        </div>
    );
}

function Accordion({ open, onToggle, title, children, testid }) {
    return (
        <div className="border border-[#E5E0D5] rounded-xl bg-white">
            <button
                data-testid={testid}
                onClick={onToggle}
                className="w-full flex items-center justify-between px-5 py-4 text-left"
            >
                <span className="font-semibold text-[#0B1437]">{title}</span>
                {open ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            {open && <div className="px-5 pb-4">{children}</div>}
        </div>
    );
}
